import { useState } from "react";
import { toast } from "sonner";
import { Check, Copy, Share2 } from "lucide-react";
import { money } from "./ambassadorApi";

const SHARE_TEXT = "Wear your identity. Pre-order Deen District's First Edition with my link:";

/** Personal link + copy/share. Locked while the account is still pending. */
export const LinkCard = ({ amb }) => {
  const [copied, setCopied] = useState(false);
  const approved = amb.status === "approved";
  const link = `${window.location.origin}/?ref=${amb.code}`; // always the site the ambassador is on (production domain)
  const copy = async () => {
    try { await navigator.clipboard.writeText(link); setCopied(true); toast.success("Link copied"); setTimeout(() => setCopied(false), 1800); } catch { toast.error("Copy failed — select the link and copy it."); }
  };
  const share = async () => {
    if (navigator.share) { try { await navigator.share({ title: "Deen District", text: SHARE_TEXT, url: link }); } catch { /* dismissed */ } } else copy();
  };
  return (
    <div data-testid="amb-link-card" className="rounded-3xl bg-[#0a0a0b] text-white p-6 sm:p-8">
      <p className="font-mono-d text-[10px] uppercase tracking-[0.25em] text-[#ff2a85]">Your personal link</p>
      {approved ? (
        <>
          <p data-testid="amb-link" className="mt-3 font-mono-d text-sm sm:text-base break-all text-white/90 select-all">{link}</p>
          <div className="mt-5 flex flex-wrap gap-2">
            <button type="button" data-testid="amb-copy-link" onClick={copy} className="inline-flex items-center gap-2 h-11 px-5 rounded-full bg-[#ff2a85] hover:bg-white hover:text-[#0a0a0b] font-mono-d text-[10px] uppercase tracking-[0.2em] transition-colors">{copied ? <Check size={13} /> : <Copy size={13} />} {copied ? "Copied" : "Copy link"}</button>
            <button type="button" data-testid="amb-share-link" onClick={share} className="inline-flex items-center gap-2 h-11 px-5 rounded-full border border-white/20 hover:border-white font-mono-d text-[10px] uppercase tracking-[0.2em] transition-colors"><Share2 size={13} /> Share</button>
          </div>
          <p className="mt-4 text-xs text-white/55 leading-relaxed">Code <span className="text-white font-bold">{amb.code}</span> · visitors are remembered for 30 days and all the way through checkout, so every piece in their bag is credited to you — even if they buy later.</p>
        </>
      ) : (
        <p data-testid="amb-link-locked" className="mt-3 text-sm text-white/70 leading-relaxed">
          {amb.status === "pending" ? "Your application is being reviewed. Your link unlocks the moment you're approved — we'll email you." : amb.status === "paused" ? "Your account is paused. Reach out via Support below if you think this is a mistake." : "Your application wasn't approved this time. You can still contact us via Support."}
        </p>
      )}
    </div>
  );
};

export const StatCards = ({ summary, amb }) => {
  const tier = amb.tier;
  const cards = [
    ["Available", money(summary.approved_cents), "approved · ready to pay out", "amb-stat-available"],
    ["Pending", money(summary.pending_cents), "unlocks when orders ship", "amb-stat-pending"],
    ["Paid out", money(summary.paid_cents), "sent to your PayPal", "amb-stat-paid"],
    ["Lifetime", money(summary.lifetime_cents), `${summary.orders} orders · ${summary.pieces} pieces · ${summary.clicks || 0} clicks`, "amb-stat-lifetime"],
  ];
  const pct = Math.min(100, Math.round((tier.sold / tier.pieces) * 100));
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        {cards.map(([l, v, s, id]) => (
          <div key={l} data-testid={id} className="rounded-2xl border border-[#e3e2dc] bg-[#faf9f6] p-4 sm:p-5 min-w-0">
            <p className="font-mono-d text-[9px] uppercase tracking-[0.2em] text-[#5f5e5a]">{l}</p>
            <p className="mt-1 font-display font-extrabold text-xl sm:text-2xl tabular-nums break-words">{v}</p>
            <p className="mt-1 text-[11px] text-[#5f5e5a] leading-snug">{s}</p>
          </div>
        ))}
      </div>
      <div data-testid="amb-tier-card" className="rounded-2xl border border-[#e3e2dc] p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center gap-4">
        <div className="min-w-0 flex-1">
          <p className="font-mono-d text-[9px] uppercase tracking-[0.2em] text-[#5f5e5a]">Your rate</p>
          <p className="mt-1 font-display font-extrabold text-xl"><span data-testid="amb-rate">{amb.rate_pct}%</span> <span className="text-sm text-[#5f5e5a] font-normal">of profit per piece</span></p>
          <p className="mt-1 text-[11px] text-[#5f5e5a]">{tier.reached ? `You've unlocked the ${tier.pct}% tier — it's yours for life.` : `${tier.to_next} more ${tier.to_next === 1 ? "piece" : "pieces"} to unlock ${tier.pct}% for life.`}</p>
        </div>
        <div className="sm:w-56">
          <div className="h-2 rounded-full bg-[#f3f2ec] overflow-hidden"><div className="h-full bg-[#ff2a85] transition-all" style={{ width: `${tier.reached ? 100 : pct}%` }} /></div>
          <p className="mt-1.5 font-mono-d text-[9px] uppercase tracking-[0.2em] text-[#5f5e5a] text-right">{Math.min(tier.sold, tier.pieces)} / {tier.pieces} pieces</p>
        </div>
      </div>
    </div>
  );
};
